// Apolo IDE — WorktreePanel (Sprint v6 B2).
//
// Shows the current worktree state of the active session. State lives in
// code_sessions.worktree_path / branch (Sprint v3 schema). The actual
// `git worktree add` happens in Electron main; this panel only displays
// + lets the user create/destroy via the same path.

import { useCallback, useEffect, useState } from 'react';
import { GitBranch, RefreshCcw, Trash2 } from 'lucide-react';
import { apiFetch } from '../../../lib/api.js';

export default function WorktreePanel({ sessionId }) {
  const [state, setState] = useState({ worktree_path: '', branch: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    const res = await apiFetch(`/api/code-ide/sessions/${sessionId}/worktree`);
    setLoading(false);
    if (!res.ok) { setError(res.error || 'Falha ao carregar worktree'); return; }
    setError(null);
    setState(res.data || { worktree_path: '', branch: '' });
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  async function handleDestroy() {
    if (!state.worktree_path) return;
    const res = await apiFetch(`/api/code-ide/sessions/${sessionId}/worktree`, { method: 'DELETE' });
    if (res.ok) load();
  }

  if (loading) {
    return (
      <div role="status" aria-live="polite" className="px-4 py-3 text-xs text-apolo-textTertiary">
        Carregando worktree…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 px-3 py-3 text-sm">
      {error && (
        <div role="alert" className="rounded-apolo-md border border-apolo-error/40 bg-apolo-errorBg p-2 text-xs text-apolo-error">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-apolo-textTertiary uppercase tracking-widest">
        <span>Worktree atual</span>
        <button type="button" onClick={load} aria-label="Atualizar worktree" className="hover:text-apolo-textSecondary">
          <RefreshCcw size={12} />
        </button>
      </div>

      {state.worktree_path ? (
        <div className="rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface2 p-3">
          <div className="flex items-center gap-2 text-apolo-textPrimary">
            <GitBranch size={14} className="text-apolo-info" aria-hidden="true" />
            <span className="font-medium">{state.branch || '(sem branch)'}</span>
          </div>
          <pre className="mt-1 overflow-x-auto text-xs text-apolo-textTertiary">{state.worktree_path}</pre>
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={handleDestroy}
              className="inline-flex items-center gap-1 rounded-apolo-md border border-apolo-error/40 px-2 py-1 text-xs text-apolo-error hover:bg-apolo-error/10 min-h-touch"
            >
              <Trash2 size={12} /> Destruir
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface2 p-4 text-center text-xs text-apolo-textTertiary">
          Nenhuma worktree ativa para esta sessão. Crie via toolbar Worktree (⇧⌘W).
        </div>
      )}
    </div>
  );
}
